import { useQuery } from "@apollo/client";
import Person from "@mui/icons-material/Person";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { ACTIVE_ORDER_STATUTE_CODES } from "encamp-shared/src/constants/echo";
import {
  HarmonizedViolationProgram,
  HarmonizedViolationResolutionStatus,
  HarmonizedViolationSeverity,
  HarmonizedViolationsQuery,
} from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import {
  extractOmnisearchValue,
  useOmnisearchActions,
  useOmnisearchStore,
} from "hooks/omnisearch-v2/omnisearchStore";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import {
  HARMONIZED_VIOLATIONS,
  LATEST_HARMONIZED_VIOLATION_REFRESH,
} from "hooks/useHarmonizedViolations";
import { FilterKey } from "hooks/useOmnisearchDatagrid";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

// Relative, not bare "util": the bare specifier resolves to Node's builtin.
import { brandColors } from "../../../util";
import {
  buildViolationColumns,
  VIOLATION_DEFAULT_COLUMN_VISIBILITY,
  ViolationRow,
} from "./columns";
import { HeldViolationsBanner } from "./HeldViolationsBanner";
import { ViolationDetailDrawer } from "./ViolationDetailDrawer";
import { DATA_FRESHNESS_TOOLTIP, formatViolationDate } from "./violationDisplay";
import { ViolationsOverviewPanel } from "./ViolationsOverviewPanel";

// The enum filters must be declared here (not derived from the columns): the
// column-derived filter path drops `enumValues`, so an enumAutocomplete built
// from a column would have no options. We therefore exclude every column except
// `facility` from the column-derived filters (below) and supply these instead.
const VIOLATION_FILTER_KEYS: FilterKey[] = [
  { key: "violationId", header: "Violation ID", filterKeyType: "text" },
  { key: "state", header: "State", filterKeyType: "text" },
  { key: "pollutant", header: "Pollutant", filterKeyType: "text" },
  { key: "businessUnit", header: "Business unit", filterKeyType: "text" },
  {
    key: "program",
    header: "Program",
    filterKeyType: "enumAutocomplete",
    enumValues: Object.values(HarmonizedViolationProgram),
  },
  {
    key: "severity",
    header: "Severity",
    filterKeyType: "enumAutocomplete",
    enumValues: Object.values(HarmonizedViolationSeverity),
  },
  {
    key: "status",
    header: "Reported status",
    filterKeyType: "enumAutocomplete",
    enumValues: Object.values(HarmonizedViolationResolutionStatus),
  },
  { key: "assignee", header: "Assignee", filterKeyType: "multipleUser" },
  {
    key: "order",
    header: "EPA Order statute",
    filterKeyType: "enumAutocomplete",
    // `orderStatuteCode` is a plain string column (the statute set may grow),
    // so the options come from the shared constant that the harmonize task's
    // ORDER_STATUTES_BY_PROGRAM map `satisfies` — widening the map without
    // widening this dropdown is a compile error, not silent drift.
    enumValues: [...ACTIVE_ORDER_STATUTE_CODES],
    // Statute codes are acronyms; the default prettifier only knows some of
    // them and would render "Cwa"/"Caa" in the dropdown and filter chips.
    enumPresentationFunction: (value) => value,
  },
];

/**
 * Filter keys for the violations grid. Staff additionally get a "Staff assignee"
 * key backed by the cross-tenant staff user picker (`multipleStaffAnyUser`).
 * Customers never receive it — the backend ignores the omnisearch key for
 * non-staff anyway, but we also keep it out of their filter popover.
 */
export function buildViolationFilterKeys(isStaff: boolean): FilterKey[] {
  if (!isStaff) return VIOLATION_FILTER_KEYS;
  return [
    ...VIOLATION_FILTER_KEYS,
    { key: "staffAssignee", header: "Staff assignee", filterKeyType: "multipleStaffAnyUser" },
  ];
}

/**
 * Toolbar button that filters the grid to violations effectively assigned to
 * the current user. Writes `assignee:<currentUserEmail>` — the same value shape
 * the `multipleUser` filter popover uses (emails), so the resulting filter chip
 * renders the user rather than a raw id. The backend handler resolves the value
 * (id or email) to an in-tenant user and matches the effective assignee.
 */
export function AssignedToMeButton({ currentUserEmail }: { currentUserEmail: string | undefined }) {
  const { editKeyValues } = useOmnisearchActions();
  if (!currentUserEmail) return null;
  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<Person />}
      sx={{ px: 2 }}
      onClick={() => editKeyValues([["assignee", currentUserEmail]])}
    >
      Assigned to me
    </Button>
  );
}

/**
 * Staff-only counterpart to {@link AssignedToMeButton}. Writes
 * `staffAssignee:<currentUserEmail>` so the grid filters to violations whose
 * effective STAFF assignee is the current user. The backend ignores the
 * `staffAssignee` omnisearch key for non-staff, and this button is only
 * rendered for staff anyway.
 *
 * Carries the staff orange (`secondary`) per the design system, so a staff-only
 * control is visually distinct from the customer-visible one beside it. Stays
 * `outlined` rather than the spec's contained orange because this is a filter
 * sitting among peer filters, not a page action.
 *
 * The label is pinned to encamp-orange[800] rather than left at `secondary.main`.
 * Measured against the page canvas (#F3F3F3): main #F08A0D is 2.27:1 and
 * secondary.dark #C35202 is 4.18:1 — both under WCAG AA's 4.5:1 for text —
 * while [800] #9F4B13 is 5.42:1. The border keeps `secondary` so the control
 * still reads as staff orange at a glance.
 */
export function StaffAssignedToMeButton({
  currentUserEmail,
}: {
  currentUserEmail: string | undefined;
}) {
  const { editKeyValues } = useOmnisearchActions();
  if (!currentUserEmail) return null;
  return (
    <Button
      size="small"
      variant="outlined"
      color="secondary"
      startIcon={<Person />}
      sx={{ px: 2, color: brandColors["encamp-orange"][800] }}
      onClick={() => editKeyValues([["staffAssignee", currentUserEmail]])}
    >
      Staff assigned to me
    </Button>
  );
}

/**
 * The omnisearch value applied by the default-filter-on-mount and the
 * "Unresolved only" toggle. Matches a `HarmonizedViolationResolutionStatus`.
 */
const UNRESOLVED_STATUS = HarmonizedViolationResolutionStatus.Unresolved;

/** True when the omnisearch `status` filter is exactly `UNRESOLVED`. */
function isUnresolvedOnlyActive(omnisearch: string): boolean {
  return extractOmnisearchValue(omnisearch, "status") === UNRESOLVED_STATUS;
}

/**
 * Toolbar control reflecting whether the grid is filtered to unresolved
 * violations only (`status:UNRESOLVED`). Checked → clicking clears the status
 * filter to show all; unchecked → clicking applies the unresolved filter.
 *
 * A checkbox rather than a button: this narrows the list, and the previous
 * primary-button treatment read as an action.
 */
export function UnresolvedOnlyCheckbox() {
  const { omnisearch } = useOmnisearchStore();
  const { editKeyValues, removeKey } = useOmnisearchActions();
  const active = isUnresolvedOnlyActive(omnisearch);
  return (
    <FormControlLabel
      sx={{ ml: 1, mr: 0, whiteSpace: "nowrap" }}
      control={
        <Checkbox
          size="small"
          checked={active}
          onChange={() =>
            active ? removeKey("status") : editKeyValues([["status", UNRESOLVED_STATUS]])
          }
        />
      }
      label={<Typography variant="body2">Unresolved only</Typography>}
    />
  );
}

/**
 * Severity first — how users actually prioritize, and previously only implicit in
 * the API's no-sort fallback, so the grid showed no sort indicator at all.
 *
 * `asc` really is highest-first: Postgres orders enum values by declaration order
 * (HIGH, MEDIUM, LOW), not alphabetically. The resolver adds `lastSeenAt desc`
 * within a severity band.
 */
const VIOLATION_DEFAULT_SORT_MODEL: GridSortModel = [{ field: "severity", sort: "asc" }];

const getItems = (data: HarmonizedViolationsQuery) => data.harmonizedViolations.items;
const getCount = (data: HarmonizedViolationsQuery) => data.harmonizedViolations.count;

/**
 * On first load with no omnisearch in the URL, default to unresolved-only so a
 * fresh landing surfaces actionable violations. A bookmarked/explicit omnisearch
 * param (even an empty toggle the user cleared) is respected and never overridden.
 */
function useDefaultUnresolvedFilter() {
  const { omnisearch } = useOmnisearchStore();
  const { editKeyValues } = useOmnisearchActions();
  const [searchParams] = useSearchParams();
  const hasAppliedDefaultFilter = useRef(false);
  useEffect(() => {
    if (hasAppliedDefaultFilter.current) return;
    if (searchParams.has("omnisearch") || omnisearch) {
      hasAppliedDefaultFilter.current = true;
      return;
    }
    hasAppliedDefaultFilter.current = true;
    editKeyValues([["status", UNRESOLVED_STATUS]]);
  }, [searchParams, omnisearch, editKeyValues]);
}

/**
 * Quiet top-right "when did this data last land" label, matching the waste
 * facility page's "Vendor data updated …". An alert banner overstated it — the
 * date is context, not a warning.
 */
export function DataFreshnessLabel({
  refresh,
}: {
  refresh: { startedAt?: string | null; finishedAt?: string | null } | null | undefined;
}) {
  const label = refresh
    ? `Violations data updated ${formatViolationDate(refresh.finishedAt ?? refresh.startedAt)}`
    : "Violations data has not been refreshed yet";
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
      <Tooltip describeChild title={refresh ? DATA_FRESHNESS_TOOLTIP : ""}>
        <Typography variant="caption" fontStyle="italic" color="text.secondary">
          {label}
        </Typography>
      </Tooltip>
    </Box>
  );
}

export default function Violations() {
  const { tenantId } = useParams();
  const [selected, setSelected] = useState<ViolationRow | null>(null);
  const { user: currentUser, isStaff } = useCurrentUser();

  useBreadcrumb({ label: "Violations" });
  useDefaultUnresolvedFilter();

  const { data: refreshData } = useQuery(LATEST_HARMONIZED_VIOLATION_REFRESH, {
    variables: { tenantId: tenantId ?? "" },
    skip: !tenantId,
  });
  const refresh = refreshData?.latestHarmonizedViolationRefresh;
  const refreshStartedAt = refresh?.startedAt ?? null;

  const columns = useMemo(
    () => buildViolationColumns(refreshStartedAt, isStaff),
    [refreshStartedAt, isStaff]
  );

  // Only `facility` is filtered via its column (a facility autocomplete).
  // program/severity/status come from VIOLATION_FILTER_KEYS; every other column
  // has no backend filter, so exclude them all from the filter popover.
  const excludeFilterColumns = useMemo(
    () => columns.filter((c) => c.field !== "facility").map((c) => c.field),
    [columns]
  );

  const buildVariables = useCallback(
    (omnisearch: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => ({
      tenantId: tenantId ?? "",
      search: omnisearch,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
    }),
    [tenantId]
  );

  const onRowClick = useCallback((params: GridRowParams<ViolationRow>) => {
    setSelected(params.row);
  }, []);

  // Rendered to the RIGHT of the omnisearch bar (DataGridToolbar places
  // `commandButtons` in the right section; `startCommandButtons` would be left).
  const commandButtons = useMemo<ReactNode[]>(
    () => [
      <AssignedToMeButton key="assigned-to-me" currentUserEmail={currentUser?.email} />,
      ...(isStaff
        ? [
            <StaffAssignedToMeButton
              key="staff-assigned-to-me"
              currentUserEmail={currentUser?.email}
            />,
          ]
        : []),
      <UnresolvedOnlyCheckbox key="unresolved-only" />,
    ],
    [currentUser?.email, isStaff]
  );

  const filterKeys = useMemo(() => buildViolationFilterKeys(isStaff), [isStaff]);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        px: 3,
        py: 2,
        // Light gray page canvas so the overview's gray metric cards read as a
        // group without needing outlines around them (or around the panel).
        bgcolor: (t) => t.palette.canvas,
      }}
    >
      <DataFreshnessLabel refresh={refresh} />
      <HeldViolationsBanner tenantId={tenantId ?? ""} />
      <ViolationsOverviewPanel tenantId={tenantId ?? ""} />
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <OmnisearchDataGridV2
          query={HARMONIZED_VIOLATIONS}
          getItems={getItems}
          getCount={getCount}
          columns={columns}
          buildVariables={buildVariables}
          onRowClick={onRowClick}
          additionalFilterKeys={filterKeys}
          commandButtons={commandButtons}
          excludeFilterColumns={excludeFilterColumns}
          initialColumnVisibilityModel={VIOLATION_DEFAULT_COLUMN_VISIBILITY}
          initialSortModel={VIOLATION_DEFAULT_SORT_MODEL}
          // The violation cell stacks description over violation code, and the
          // assignee cell stacks name over "(facility default)". The 38px default
          // clipped both; 56 lets a two-line cell breathe.
          rowHeight={56}
          persistenceKey="harmonized-violations-grid"
          tenantId={tenantId}
          noRowsMessage="No violations have been recorded for this organization."
        />
      </Box>
      <ViolationDetailDrawer
        violation={selected}
        tenantId={tenantId ?? ""}
        refreshStartedAt={refreshStartedAt}
        onClose={() => setSelected(null)}
      />
    </Box>
  );
}
